import type { DecimalOdds, Handicap, Odds } from '@ps/isp-web-services-types';
import { getWindow } from '@ps/web-core-utils';
import { useCallback, useEffect, useRef, useState } from 'react';

const secureWindow = getWindow();

export enum OddsChangeDirection {
  Increase,
  Decrease
}

export interface OddsChange {
  handicap: Handicap;
  odds?: Odds;
  time: string;
  direction?: OddsChangeDirection;
}

const addToOddsHistory =
  (direction: OddsChangeDirection, latestOdds: Odds | undefined, handicap: Handicap) =>
  (currentHistory: OddsChange[]) => [
    ...currentHistory,
    {
      handicap,
      odds: latestOdds,
      time: new Date().toISOString(),
      direction
    }
  ];

export const CLEAN_HISTORY_TIMEOUT = 4000;

const useSelectionWatcher = (odds: Odds | undefined, handicap: Handicap = 0) => {
  const [oddsHistory, setOddsHistory] = useState<OddsChange[]>([
    { handicap, odds, time: new Date().toISOString() }
  ]);

  const oddsTimeouts = useRef<number[]>([]);
  useEffect(
    () => () => secureWindow && oddsTimeouts.current.forEach(secureWindow.clearTimeout),
    []
  );

  const removeOddsHistory = useCallback(() => {
    const timer = secureWindow?.setTimeout(() => {
      setOddsHistory((currentHistory) => currentHistory.slice(1));
    }, CLEAN_HISTORY_TIMEOUT);
    timer && oddsTimeouts.current.push(timer);
  }, []);

  const onOddsIncrease = useCallback(
    (latestOdds: Odds, handicap: Handicap) => {
      setOddsHistory(addToOddsHistory(OddsChangeDirection.Increase, latestOdds, handicap));
      removeOddsHistory();
    },
    [removeOddsHistory]
  );

  const onOddsDecrease = useCallback(
    (latestOdds: Odds | undefined, handicap: Handicap) => {
      setOddsHistory(addToOddsHistory(OddsChangeDirection.Decrease, latestOdds, handicap));
      removeOddsHistory();
    },
    [removeOddsHistory]
  );

  const prevOdds = useRef<DecimalOdds['decimalOdds'] | undefined>(
    odds?.decimalDisplayOdds?.decimalOdds
  );
  const prevHandicap = useRef<Handicap>(handicap);

  useEffect(() => {
    if (odds?.decimalDisplayOdds && !prevOdds.current) {
      // Handles odds moving from SP to LP
      onOddsIncrease(odds, handicap);
    } else if (!odds?.decimalDisplayOdds && prevOdds.current) {
      // Handles odds moving from LP to SP
      onOddsDecrease(undefined, handicap);
    } else if (odds?.decimalDisplayOdds && prevOdds.current) {
      if (prevOdds.current > odds.decimalDisplayOdds.decimalOdds) {
        onOddsDecrease(odds, handicap);
      } else if (prevOdds.current < odds.decimalDisplayOdds.decimalOdds) {
        onOddsIncrease(odds, handicap);
      }
    }

    prevOdds.current = odds?.decimalDisplayOdds?.decimalOdds;
  }, [odds, odds?.decimalDisplayOdds?.decimalOdds, onOddsDecrease, onOddsIncrease, handicap]);

  useEffect(() => {
    if (!odds) return;

    if (prevHandicap.current > handicap) {
      onOddsDecrease(odds, handicap);
    } else if (prevHandicap.current < handicap) {
      onOddsIncrease(odds, handicap);
    }
    prevHandicap.current = handicap;
  }, [handicap, odds, onOddsDecrease, onOddsIncrease]);

  return oddsHistory;
};

export default useSelectionWatcher;
